import React, { useState, useEffect } from 'react';
import { Mail, Layers, AlertTriangle, Save, Wand2 } from 'lucide-react';
import { apiSetCampaignMailboxes } from '../../api';
import { ActionButton } from './parts';
import styles from './Mailer.module.css';

/**
 * Which mailboxes a campaign sends from, and how much of each it may use.
 *
 * The two modes are not two mechanisms — a single mailbox is just a pool of
 * one, and the scheduler runs the same code either way. The choice is presented
 * because it is a real editorial decision:
 *
 *   One mailbox   everything on one domain's reputation, one persona, volume
 *                 fully attributable to that domain. Right for a sequence tied
 *                 to a named sender, and right while a domain is being warmed.
 *
 *   Several       the same sequence spread across domains, each with its own
 *                 stated share. Right when the list is bigger than one address
 *                 can safely carry, which is the usual reason to buy a second
 *                 domain in the first place.
 *
 * The number that matters on this screen is not the campaign total — it is what
 * is still free on each mailbox after every *other* running campaign has taken
 * its share. That is the ceiling for the box you are typing in, so it is printed
 * next to it rather than left to be discovered by a validation error.
 */
const SendingFrom = ({ jwtToken, campaignId, allocations = [], mailboxes = [], sendMode, onSaved, report }) => {
  const [mode, setMode] = useState(sendMode || 'specific');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows(allocations.map((a) => ({
      mailbox: String(a.mailbox),
      dailyCap: a.dailyCap,
      active: a.active !== false,
    })));
    setMode(sendMode || 'specific');
  }, [allocations, sendMode]);

  const available = mailboxes.filter((m) => m.enabled);
  const chosen = new Set(rows.map((r) => r.mailbox));

  /** Headroom on a mailbox once every other running campaign has taken its share. */
  const freeOn = (mailboxId) => {
    const box = mailboxes.find((m) => String(m._id) === String(mailboxId));
    if (!box) return 0;
    const mine = allocations.find((a) => String(a.mailbox) === String(mailboxId));
    // Our own current claim is added back: raising 20 to 30 should be measured
    // against the mailbox's whole limit, not against what is left after us.
    return Math.max(0, box.limitToday - box.allocated + (mine?.dailyCap || 0));
  };

  const total = rows.filter((r) => r.active).reduce((sum, r) => sum + (Number(r.dailyCap) || 0), 0);

  const setRow = (mailboxId, changes) =>
    setRows((list) => list.map((r) => (r.mailbox === mailboxId ? { ...r, ...changes } : r)));

  const addRow = (mailboxId) => {
    if (!mailboxId || chosen.has(mailboxId)) return;
    // Default to what is actually free rather than a round number that will
    // immediately fail validation — capped at 20, which is the sane per-mailbox
    // ceiling for cold outreach whatever the limit permits.
    const suggested = Math.min(20, freeOn(mailboxId));
    setRows((list) => [...list, { mailbox: mailboxId, dailyCap: suggested, active: true }]);
    if (mode === 'specific' && rows.length >= 1) setMode('pool');
  };

  const removeRow = (mailboxId) => setRows((list) => list.filter((r) => r.mailbox !== mailboxId));

  const switchMode = (next) => {
    setMode(next);
    if (next === 'specific' && rows.length > 1) setRows(rows.slice(0, 1));
  };

  /** Even split of a target total across the chosen mailboxes. */
  const spreadEvenly = () => {
    const active = rows.filter((r) => r.active);
    if (!active.length) return;
    const perBox = active.map((r) => freeOn(r.mailbox));
    const fair = Math.min(...perBox, 20);
    setRows((list) => list.map((r) => (r.active ? { ...r, dailyCap: fair } : r)));
    report?.ok(`${fair} a day from each — ${fair * active.length} in total.`);
  };

  const save = async () => {
    try {
      const result = await apiSetCampaignMailboxes(jwtToken, campaignId, rows);
      if (result.warnings?.length) report?.warn(result.warnings.join(' '));
      else report?.ok(`Sending from ${rows.length} mailbox${rows.length === 1 ? '' : 'es'} — ${result.perDay}/day.`);
      await onSaved?.();
    } catch (e) {
      report?.fail(e);
      throw e;
    }
  };

  const overCommitted = rows.filter((r) => r.active && Number(r.dailyCap) > freeOn(r.mailbox));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h3 className="card-title" style={{ marginBottom: 4 }}>Sending from</h3>
      <p className="page-subtitle" style={{ marginBottom: 16 }}>
        Reputation is tracked per domain, so where a campaign sends from — and how much it takes
        from each address — is the setting that decides whether it lands.
      </p>

      <div className={styles.modeChoice}>
        <button
          type="button"
          className={`${styles.modeCard} ${mode === 'specific' ? styles.modeCardActive : ''}`}
          onClick={() => switchMode('specific')}
        >
          <Mail size={18} />
          <div className={styles.modeTitle}>One mailbox</div>
          <div className={styles.modeHelp}>
            Everything from a single address, on one domain's reputation. Best for a sequence tied to
            a person, and while a new domain is warming up.
          </div>
        </button>
        <button
          type="button"
          className={`${styles.modeCard} ${mode === 'pool' ? styles.modeCardActive : ''}`}
          onClick={() => switchMode('pool')}
        >
          <Layers size={18} />
          <div className={styles.modeTitle}>Several mailboxes</div>
          <div className={styles.modeHelp}>
            Spread the same sequence across domains, each with its own daily share. The queue divides
            itself; follow-ups stay on whichever address started the thread.
          </div>
        </button>
      </div>

      {rows.length > 0 && (
        <div className={styles.allocTable}>
          <div className={styles.allocHeader}>
            <span>Mailbox</span>
            <span>Per day</span>
            <span>Free there</span>
            <span>Sent today</span>
            <span />
          </div>

          {rows.map((row) => {
            const box = mailboxes.find((m) => String(m._id) === String(row.mailbox));
            const live = allocations.find((a) => String(a.mailbox) === String(row.mailbox));
            const free = freeOn(row.mailbox);
            const over = row.active && Number(row.dailyCap) > free;

            return (
              <div key={row.mailbox} className={`${styles.allocRow} ${row.active ? '' : styles.allocRowOff}`}>
                <div className={styles.allocMailbox}>
                  <div className={styles.allocAddress}>{box?.address || '(removed)'}</div>
                  <div className={styles.allocDomain}>
                    {box?.domain}
                    {box?.warmupActive && ' · warming up'}
                    {box && !box.enabled && ' · switched off'}
                  </div>
                </div>

                <input
                  className={`input ${styles.allocInput} ${over ? styles.allocInputOver : ''}`}
                  type="number"
                  min="0"
                  max="500"
                  value={row.dailyCap}
                  disabled={!row.active}
                  onChange={(e) => setRow(row.mailbox, { dailyCap: Number(e.target.value) })}
                />

                <span className={over ? styles.allocOver : styles.allocMuted}>
                  {free}
                  {over && <> — too many</>}
                </span>

                <span className={styles.allocMuted}>
                  {live ? `${live.sentToday} / ${live.capToday}` : '—'}
                </span>

                <div className={styles.allocActions}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setRow(row.mailbox, { active: !row.active })}
                    title={row.active ? 'Take this domain out of rotation, keeping the number' : 'Put it back in'}
                  >
                    {row.active ? 'Pause' : 'Resume'}
                  </button>
                  {rows.length > 1 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeRow(row.mailbox)}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className={styles.allocTotal}>
            <strong>{total}</strong> emails a day for this campaign
            {rows.filter((r) => r.active).length > 1 && (
              <span className={styles.allocMuted}>
                {' '}— {rows.filter((r) => r.active).map((r) => r.dailyCap).join(' + ')}
              </span>
            )}
          </div>
        </div>
      )}

      {overCommitted.length > 0 && (
        <div className={styles.warnBox}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> More than those mailboxes have left</div>
          Other running campaigns have already claimed part of{' '}
          {overCommitted.map((r) => mailboxes.find((m) => String(m._id) === String(r.mailbox))?.address).join(', ')}.
          Saving is allowed, but starting is not — either lower these numbers, or raise the mailbox
          limits on the Mailboxes tab.
        </div>
      )}

      <div className={styles.toolbar} style={{ marginBottom: 0, marginTop: 14 }}>
        {(mode === 'pool' || rows.length === 0) && (
          <select
            className="input"
            style={{ width: 'auto', maxWidth: 280 }}
            value=""
            onChange={(e) => addRow(e.target.value)}
          >
            <option value="">Add a mailbox…</option>
            {available
              .filter((m) => !chosen.has(String(m._id)))
              .map((m) => (
                <option key={m._id} value={m._id}>
                  {m.address} — {freeOn(m._id)}/day free
                </option>
              ))}
          </select>
        )}

        {mode === 'specific' && rows.length > 0 && (
          <select
            className="input"
            style={{ width: 'auto', maxWidth: 280 }}
            value={rows[0].mailbox}
            onChange={(e) => setRows([{ ...rows[0], mailbox: e.target.value }])}
          >
            {available.map((m) => (
              <option key={m._id} value={m._id}>{m.address}</option>
            ))}
          </select>
        )}

        {rows.filter((r) => r.active).length > 1 && (
          <button type="button" className="btn btn-ghost" onClick={spreadEvenly}>
            <Wand2 size={15} /> Split evenly
          </button>
        )}

        <div className={styles.spacer} />

        <ActionButton
          className="btn btn-primary"
          onClick={save}
          icon={<Save size={16} />}
          busyLabel="Saving…"
          doneLabel="Saved"
          failLabel="Not saved"
          disabled={rows.length === 0}
        >
          Save sending setup
        </ActionButton>
      </div>

      {available.length === 0 && (
        <div className={styles.warnBox} style={{ marginTop: 14, marginBottom: 0 }}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> No mailboxes available</div>
          Connect at least one on the Mailboxes tab before this campaign can send.
        </div>
      )}

      {rows.length > 1 && (
        <p className="text-sm text-muted" style={{ marginTop: 14, marginBottom: 0, lineHeight: 1.6 }}>
          The queue divides itself: each mailbox takes contacts until its share for the day is spent,
          so a total of {total} goes out as {rows.filter((r) => r.active).map((r) => r.dailyCap).join('/')} across
          the domains. A contact stays with the address that first emailed them, so every follow-up
          arrives in the same thread from the same person.
        </p>
      )}
    </div>
  );
};

export default SendingFrom;
